import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Typography,
  FormLabel,
  TextField,
  Divider,
  Alert,
  Button,
  Box,
  InputAdornment,
  Switch,
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useI18nForm from '../../components/common/hooks/useI18nForm';
import ProfileForm from 'components/common/Forms/ProfileForm';
import useAuth0 from 'common/hooks/useAuth0';
import {
  GetMfaSmsStatus,
  GetProfile,
  ResetMfaSms,
  ResetPassword,
  UpdateProfile,
} from '../../services/profile/ProfileService';
import { Check, Close, ErrorOutline, LockOutlined } from '@mui/icons-material';
import { FormatDefaultValuePhoneNumber, UnFormatValuePhoneNumber } from '../../components/common/FieldFormat';
import useApi, { Status } from 'common/hooks/useApi';
import LoadingIndicator from 'components/common/LoadingIndicator';
import { createUpdateProfilePayload } from 'services/profile/UpdateProfilePayload';
import {
  firstNameYupValidator,
  homePhoneYupValidator,
  lastNameYupValidator,
  mobilePhoneYupValidator,
} from 'components/common/YupValidations';
import { AlertColor } from '@mui/material/Alert/Alert';
import { Helmet } from 'react-helmet';
import { SyncAccount } from 'components/common/syncAccount';

interface AccountProfileFormValues {
  FirstName: string;
  LastName: string;
  Email: string;
  HomeNumber: string;
  MobileNumber: string;
}

const AccountProfile = (): JSX.Element => {
  const { t, i18n } = useTranslation('component');
  const { user } = useAuth0();
  const [saveMessage, setSaveMessage] = useState({ message: '', type: '' });
  const [isSaving, setSaving] = useState(false);
  const [isChangingPassword, setChangingPassword] = useState(false);
  const [isResettingMfa, setResettingMfa] = useState(false);
  const [isDisableMfaDialogOpen, setDisableMfaDialogOpen] = useState(false);
  const [fetchProfile, profile, fetchingProfileStatus] = useApi(GetProfile);
  const [fetchMfaStatus, mfaStatus] = useApi(GetMfaSmsStatus);
  const [profileInfo, setProfileInfo] = useState<AccountProfileFormValues | null>(null);

  useEffect(() => {
    if (user?.session_id) {
      fetchProfile(user?.session_id);
      fetchMfaStatus(user?.sub);
    }
  }, [fetchProfile, fetchMfaStatus, user.session_id, user.sub]);

  useEffect(() => {
    if (profile) {
      setProfileInfo({
        FirstName: profile?.Firstname,
        LastName: profile?.Lastname,
        Email: profile?.Email,
        HomeNumber: FormatDefaultValuePhoneNumber(profile?.Phones?.OtherPhone),
        MobileNumber: FormatDefaultValuePhoneNumber(profile?.Phones?.CellPhone),
      });
    }
  }, [profile]);

  const isDataLoading = fetchingProfileStatus === Status.Pending;
  const hasFetchDataFailed = fetchingProfileStatus === Status.Rejected;
  const isDataResolved = fetchingProfileStatus === Status.Resolved;

  const ChangePassword = async () => {
    try {
      setChangingPassword(true);
      const getProfileInfoResponse = await ResetPassword(user!.email);
      if (getProfileInfoResponse.status === 200) {
        setSaveMessage({
          message: t('AccountProfile.PasswordChangeSuccessMessage', { email: user!.email }) as string,
          type: 'info',
        });
      } else {
        throw 'error';
      }
    } catch (e) {
      setSaveMessage({ message: t('Errors.GenericError') as string, type: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  const ResetMfa = async () => {
    try {
      setResettingMfa(true);
      await ResetMfaSms(user!.sub);
      setSaveMessage({ message: t('AccountProfile.MfaResetSuccess') as string, type: 'info' });
      fetchMfaStatus(user!.sub);
    } catch (e) {
      setSaveMessage({ message: t('Errors.GenericError') as string, type: 'error' });
    } finally {
      setResettingMfa(false);
    }
  };

  const handleSmsToggleClick = () => {
    if (mfaStatus === true) {
      setDisableMfaDialogOpen(true);
    }
  };

  const handleConfirmDisableMfa = async () => {
    setDisableMfaDialogOpen(false);
    await ResetMfa();
  };

  const handleCancelDisableMfa = () => {
    setDisableMfaDialogOpen(false);
  };

  const handleSubmit = async function (values: AccountProfileFormValues) {
    setSaveMessage({ message: '', type: '' });
    setSaving(true);

    try {
      values.HomeNumber = UnFormatValuePhoneNumber(values.HomeNumber);
      values.MobileNumber = UnFormatValuePhoneNumber(values.MobileNumber);

      const updateProfilePayload = createUpdateProfilePayload(profile);
      updateProfilePayload.Firstname = values.FirstName;
      updateProfilePayload.Lastname = values.LastName;
      updateProfilePayload.Phones.CellPhone = values.MobileNumber;
      updateProfilePayload.Phones.OtherPhone = values.HomeNumber;

      await UpdateProfile(user!.session_id, updateProfilePayload);
      SyncAccount();
      setSaveMessage({ message: t('AccountProfile.saveSuccess') as string, type: 'success' });
    } catch (e) {
      setSaveMessage({ message: t('Errors.GenericError') as string, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const validationSchema = Yup.object().shape(
    {
      FirstName: firstNameYupValidator(t),
      LastName: lastNameYupValidator(t),
      HomeNumber: homePhoneYupValidator(t, i18n),
      MobileNumber: mobilePhoneYupValidator(t, i18n),
      Email: Yup.string(),
    },
    [['HomeNumber', 'MobileNumber']]
  );

  const initialValues: AccountProfileFormValues = {
    FirstName: profileInfo?.FirstName == null ? '' : profileInfo.FirstName!,
    LastName: profileInfo?.LastName == null ? '' : profileInfo.LastName!,
    Email: profileInfo?.Email == null ? '' : profileInfo.Email!,
    HomeNumber: FormatDefaultValuePhoneNumber(profileInfo?.HomeNumber == undefined ? '' : profileInfo.HomeNumber!),
    MobileNumber: FormatDefaultValuePhoneNumber(
      profileInfo?.MobileNumber == undefined ? '' : profileInfo.MobileNumber!
    ),
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values: AccountProfileFormValues) => {
      handleSubmit(values);
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.SettingTitle')}</title>
        <meta name="description" content={t('MetaTag.SettingDescription').toString()} />
      </Helmet>
      <Typography variant="h1" sx={{ mb: '24px' }}>
        {t('AccountProfile.Title')}
      </Typography>

      {isDataLoading && <LoadingIndicator />}

      {hasFetchDataFailed && (
        <Alert id="fetchAlert" severity="error" sx={{ mb: '16px' }}>
          {t('AccountProfile.fetchDataFailed')}
        </Alert>
      )}

      {saveMessage.message && (
        <Alert id="alertMessage" severity={saveMessage.type as AlertColor} sx={{ mb: '16px' }}>
          {saveMessage.message}
        </Alert>
      )}

      {isDataResolved && (
        <form noValidate onSubmit={formik.handleSubmit}>
          <Typography variant="h2" sx={{ fontSize: '18px', fontWeight: 700, mt: 1, mb: 2 }}>
            {t('AccountProfile.PersonalInfoSection')}
          </Typography>

          <Grid container spacing={2}>
            <ProfileForm isCreating={false} formik={formik} showEmail={false} />
          </Grid>

          <Typography variant="h2" sx={{ fontSize: '18px', fontWeight: 700, mt: 4, mb: 2 }}>
            {t('AccountProfile.SecuritySection')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormLabel htmlFor="EmailFormInput">{t('Form.Email')}</FormLabel>
              <TextField
                required
                disabled
                fullWidth
                id="EmailFormInput"
                name="Email"
                autoComplete="email"
                placeholder="example@familiprix.com"
                value={formik.values.Email}
                onChange={formik.handleChange}
                error={formik.touched.Email && Boolean(formik.errors.Email)}
                helperText={formik.touched.Email && formik.errors.Email}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <LockOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel htmlFor="Password">{t('Form.Password')}</FormLabel>
              <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    disabled
                    required
                    fullWidth
                    name="Password"
                    type="password"
                    id="Password"
                    placeholder="*****************"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockOutlined fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                    <Box minWidth={{ xs: '34px', sm: '50px' }}>
                      {isChangingPassword && (
                        <LoadingIndicator
                          sx={{
                            margin: '0 10px 0 0',
                            marginLeft: { xs: '0', sm: '16px' },
                          }}
                        />
                      )}
                    </Box>
                    <Button
                      id="btnChangePassword"
                      variant="outlined"
                      onClick={ChangePassword}
                      disabled={isChangingPassword}
                      sx={{ fontSize: '13px', flex: 1, lineHeight: '28px', fontWeight: 500, padding: '6px 16px' }}>
                      {t('AccountProfile.PasswordChangeButton')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormLabel>{t('AccountProfile.MfaLabel')}</FormLabel>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: '4px', mb: '8px' }}>
                {t('AccountProfile.MfaDescription')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="body2">{t('AccountProfile.MfaSmsLabel')}</Typography>
                <Switch
                  id="smsMfaToggle"
                  checked={mfaStatus === true}
                  onClick={handleSmsToggleClick}
                  disabled={isResettingMfa || mfaStatus !== true}
                  inputProps={{ 'aria-label': t('AccountProfile.MfaSmsLabel') as string }}
                  icon={
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <LockOutlined sx={{ fontSize: 10, color: '#9CA3AF' }} />
                    </Box>
                  }
                  checkedIcon={
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Check sx={{ fontSize: 10, color: 'primary.main', strokeWidth: 2 }} />
                    </Box>
                  }
                  sx={{
                    width: 38,
                    height: 20,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                      margin: '2px',
                      transitionDuration: '200ms',
                      '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: 'primary.main',
                          opacity: 1,
                          border: 0,
                        },
                      },
                      '&.Mui-disabled + .MuiSwitch-track': {
                        opacity: 1,
                      },
                      '&.Mui-disabled .MuiSwitch-thumb': {
                        color: '#FFFFFF',
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      boxSizing: 'border-box',
                      width: 16,
                      height: 16,
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 20 / 2,
                      backgroundColor: '#DDE3EB',
                      opacity: 1,
                    },
                  }}
                />
                {isResettingMfa && <LoadingIndicator sx={{ margin: '0 10px' }} />}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right', mt: 2 }}>
            {isSaving && <LoadingIndicator sx={{ margin: '0 16px 0 0' }} />}
            <Button type="submit" variant="contained" disabled={isSaving}>
              {t('AccountProfile.SubmitButton')}
            </Button>
          </Box>
        </form>
      )}

      <Dialog
        id="disableMfaDialog"
        open={isDisableMfaDialogOpen}
        onClose={handleCancelDisableMfa}
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px', maxWidth: '400px' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton aria-label="close" onClick={handleCancelDisableMfa} sx={{ color: 'text.secondary' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '-8px', mb: '8px' }}>
          <ErrorOutline sx={{ fontSize: '32px', color: 'primary.main' }} />
        </Box>
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 700,
            padding: '8px 24px 16px 24px',
          }}>
          {t('AccountProfile.MfaDisableConfirmTitle')}
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            id="btnConfirmDisableMfa"
            variant="contained"
            onClick={handleConfirmDisableMfa}
            disabled={isResettingMfa}>
            {t('AccountProfile.MfaDisableConfirmButton')}
          </Button>
          <Button id="btnCancelDisableMfa" variant="outlined" onClick={handleCancelDisableMfa}>
            {t('AccountProfile.MfaDisableCancelButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountProfile;
